import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthBridge';
import { 
  ClinicalSimulatorScenario, 
  SimulatorHotspot, 
  SimulatorHotspotAction, 
  SimulatorInstantTrap,
  SimulatorHotspotCoordinates 
} from '../../types';
import { useThemeTokens } from '../../hooks/useActiveTheme';
import { 
  CLINICAL_ENVIRONMENTS, 
  CLINICAL_SKILL_FIXTURES, 
  buildSmartClinicalPrompt, 
  HANDWASHING_HOTSPOTS_PRESET,
  coords2DtoSpherical,
  sphericalTo2DCoords
} from '../../data/clinicalEnvironments';
import { ClinicalPhotosphereStage } from './ClinicalPhotosphereStage';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  ShieldAlert, 
  User, 
  Sparkles, 
  RotateCcw, 
  Play, 
  Eye, 
  Heart, 
  Gauge, 
  Layers, 
  ChevronRight, 
  Volume2, 
  VolumeX, 
  Check, 
  Award,
  Droplets,
  Lock,
  Unlock,
  Footprints,
  Bell,
  ShieldCheck,
  Sliders,
  Maximize2,
  HelpCircle,
  Box,
  BedDouble,
  Shield,
  Zap,
  CheckCheck,
  RefreshCw,
  Compass,
  ListChecks,
  Image as ImageIcon,
  MapPin,
  Info,
  Download,
  Upload,
  Crosshair,
  Globe,
  Trash2,
  Plus,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  FileImage,
  CheckCircle
} from 'lucide-react';

interface ClinicalRoomStageProps {
  scenario: ClinicalSimulatorScenario;
  onExit?: () => void;
  isAdminTestingMode?: boolean;
  onScenarioChange?: (updated: ClinicalSimulatorScenario) => void;
  initialTourMode?: boolean;
}

// Web Audio API synthesizer for clinical feedback, telemetry, and room equipment
const playFeedbackChime = (type: 'success' | 'warning' | 'fail' | 'click' | 'latch' | 'water') => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    if (type === 'success') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, now); // D5
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.15); // A5
      gain.gain.setValueAtTime(0.12, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.start(now);
      osc.stop(now + 0.25);
    } else if (type === 'warning') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(320, now);
      osc.frequency.setValueAtTime(240, now + 0.12);
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.start(now);
      osc.stop(now + 0.25);
    } else if (type === 'fail') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(180, now);
      osc.frequency.exponentialRampToValueAtTime(90, now + 0.35);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      osc.start(now);
      osc.stop(now + 0.4);
    } else if (type === 'click') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(700, now);
      gain.gain.setValueAtTime(0.05, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
      osc.start(now);
      osc.stop(now + 0.05);
    } else if (type === 'latch') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(280, now);
      gain.gain.setValueAtTime(0.07, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
      osc.start(now);
      osc.stop(now + 0.08);
    } else if (type === 'water') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.linearRampToValueAtTime(520, now + 0.2);
      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
      osc.start(now);
      osc.stop(now + 0.3);
    }
  } catch (e) {
    // AudioContext blocked or not supported
  }
};

export const ClinicalRoomStage: React.FC<ClinicalRoomStageProps> = ({
  scenario,
  onExit,
  isAdminTestingMode = false,
  onScenarioChange,
  initialTourMode = false,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useThemeTokens();
  const imageStageRef = useRef<HTMLDivElement>(null);

  // Environment Config & Image
  const envConfig = CLINICAL_ENVIRONMENTS[scenario.environment] || CLINICAL_ENVIRONMENTS.bedside_room;

  // Check if 360° Photosphere is supported:
  // 1) Explicit scenario flag (default true unless explicitly false)
  // 2) Or scenario / environment has a photosphereUrl
  const is360Available = scenario.enable3dPhotosphere !== false || Boolean(scenario.photosphereUrl) || Boolean(envConfig?.photosphereUrl);

  // View Mode: 'photosphere' (3D 360 Photosphere) vs 'photorealistic' (Image with hotspot pins) vs 'grid' (Structured procedural step cards)
  const [stageViewMode, setStageViewMode] = useState<'photosphere' | 'photorealistic' | 'grid'>(
    initialTourMode ? 'photosphere' : ((scenario.enable3dPhotosphere !== false && is360Available) ? 'photosphere' : 'photorealistic')
  );

  // Super Admin Hotspot Customization & Calibration State
  const [isCalibratingHotspots, setIsCalibratingHotspots] = useState(false);
  const [calibratingHotspotId, setCalibratingHotspotId] = useState<string | null>(null);

  // Super Admin Image Customization & Synthesis State
  const [isAiImageModalOpen, setIsAiImageModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiEnvironment, setAiEnvironment] = useState<'bedside_room' | 'exam_room' | 'phlebotomy_chair' | 'icu_bay' | 'handwashing_station' | string>(scenario.environment || 'bedside_room');
  const [selectedAiComponents, setSelectedAiComponents] = useState<string[]>([
    'candidate', 'sink', 'faucet', 'soap', 'towels', 'waste_bin', 'hygiene_poster'
  ]);
  const [isGeneratingAiImage, setIsGeneratingAiImage] = useState(false);
  const [generatedAiImageUrl, setGeneratedAiImageUrl] = useState<string | null>(null);
  const [is360PanoramaMode, setIs360PanoramaMode] = useState(false);
  const [wixUrlInput, setWixUrlInput] = useState(scenario.customImageUrl || scenario.imageUrl || '');
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeImageUrl = scenario.customImageUrl || scenario.imageUrl || envConfig.imageUrl || '/images/environments/bedside_room.jpg';
  const activePhotosphereUrl = scenario.photosphereUrl || scenario.customImageUrl || scenario.imageUrl || envConfig.photosphereUrl || activeImageUrl;

  // Active Station & Actions State
  const [activeHotspot, setActiveHotspot] = useState<SimulatorHotspot | null>(null);
  const [completedActionIds, setCompletedActionIds] = useState<string[]>([]);
  const [actionDialogueLogs, setActionDialogueLogs] = useState<Array<{ text: string; category: string; isCritical?: boolean; time: string }>>([]);
  const [residentSpeech, setResidentSpeech] = useState<string | null>(null);

  // Interactive Bedside State
  const [simState, setSimState] = useState<{
    curtainDrawn: boolean;
    bedHeight: 'low' | 'working' | 'high';
    bedLocked: boolean;
    handsClean: boolean;
    glovesOn: boolean;
    nonSkidShoesOn: boolean;
    callLightInReach: boolean;
    gaitBeltApplied: boolean;
    residentComfort: number;
  }>({
    curtainDrawn: scenario.initialState?.curtainDrawn || false,
    bedHeight: scenario.initialState?.bedHeight || 'low',
    bedLocked: scenario.initialState?.bedLocked || false,
    handsClean: scenario.initialState?.handsClean || false,
    glovesOn: scenario.initialState?.glovesOn || false,
    nonSkidShoesOn: scenario.initialState?.nonSkidShoesOn || false,
    callLightInReach: scenario.initialState?.callLightInReach || false,
    gaitBeltApplied: scenario.initialState?.gaitBeltApplied || false,
    residentComfort: scenario.initialState?.residentComfort || 90
  });

  // Safety Verification Trackers
  const [idBandVerified, setIdBandVerified] = useState(false);
  const [fowlersAngle, setFowlersAngle] = useState<'flat' | 'semi' | 'high'>('semi');

  // Examination Timer & Status
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showDebrief, setShowDebrief] = useState(false);
  const [triggeredDisqualification, setTriggeredDisqualification] = useState<SimulatorInstantTrap | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(true);

  // Visual touch ripple state
  const [touchRipples, setTouchRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const [activePhaseIndex, setActivePhaseIndex] = useState<number>(0);

  // Timer Tick
  useEffect(() => {
    if (isCompleted || triggeredDisqualification) return;
    const interval = setInterval(() => {
      setSecondsElapsed(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isCompleted, triggeredDisqualification]);

  // Cancel speech synthesis on unmount, tab/viewMode change, or simulator exit
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, [stageViewMode]);

  const handleExitSimulator = useCallback(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    if (onExit) {
      onExit();
    }
  }, [onExit]);

  // Sound wrapper
  const sound = (type: 'success' | 'warning' | 'fail' | 'click' | 'latch' | 'water') => {
    if (audioEnabled) {
      playFeedbackChime(type);
    }
  };

  // Helper: Format seconds to MM:SS
  const formatTime = (secs: number) => {
    const mins = Math.floor(secs / 60);
    const remaining = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${remaining.toString().padStart(2, '0')}`;
  };

  // Resolve Hotspot Pin Coordinates (uses authored coordinates or calibrated default for this environment)
  const getHotspotCoordinates = (hotspot: SimulatorHotspot): { x: number; y: number } => {
    if (hotspot.coordinates && typeof hotspot.coordinates.x === 'number' && typeof hotspot.coordinates.y === 'number') {
      return { x: hotspot.coordinates.x, y: hotspot.coordinates.y };
    }
    const defaultCoords = envConfig.defaultHotspotCoordinates[hotspot.zone];
    if (defaultCoords) {
      return { x: defaultCoords.x, y: defaultCoords.y };
    }
    // Fallback spread
    return { x: 50, y: 50 };
  };

  // Procedural Phased Steps Breakdown
  const proceduralPhases = [
    {
      id: 'prep',
      name: '1. Infection Control & Prep',
      description: 'Hand hygiene, PPE, clean barriers, supply inspection',
      zones: ['sink', 'supplies']
    },
    {
      id: 'greeting',
      name: '2. Resident Greeting & Dignity',
      description: 'Knock, greet by name, verify ID wristband, privacy curtain',
      zones: ['resident', 'privacy_curtain']
    },
    {
      id: 'execution',
      name: '3. Skill Execution',
      description: 'Bed ergonomics, positioning, transfer/technique milestones',
      zones: ['bed', 'resident', 'supplies']
    },
    {
      id: 'exit',
      name: '4. Exit Safety Protocol',
      description: 'Lock brakes, lower bed, place call light, final handwash',
      zones: ['call_light', 'bed', 'sink']
    }
  ];

  // Calculate 4-Pillar Score
  const calculateScores = () => {
    let infectionEarned = 0;
    let dignityEarned = 0;
    let safetyEarned = 0;
    let techniqueEarned = 0;

    const rubric = scenario?.rubricMilestones || {
      infectionControl: { totalWeight: 25 },
      residentDignity: { totalWeight: 25 },
      safetyMechanics: { totalWeight: 25 },
      proceduralTechnique: { totalWeight: 25 }
    };
    let infectionTotal = rubric.infectionControl?.totalWeight || 25;
    let dignityTotal = rubric.residentDignity?.totalWeight || 25;
    let safetyTotal = rubric.safetyMechanics?.totalWeight || 25;
    let techniqueTotal = rubric.proceduralTechnique?.totalWeight || 25;

    (scenario?.hotspots || []).forEach(hotspot => {
      (hotspot?.availableActions || []).forEach(action => {
        if (completedActionIds.includes(action.actionId)) {
          if (action.category === 'hygiene') infectionEarned += action.pointsAwarded;
          else if (action.category === 'dignity') dignityEarned += action.pointsAwarded;
          else if (action.category === 'safety') safetyEarned += action.pointsAwarded;
          else if (action.category === 'technique') techniqueEarned += action.pointsAwarded;
        }
      });
    });

    infectionEarned = Math.min(infectionEarned, infectionTotal);
    dignityEarned = Math.min(dignityEarned, dignityTotal);
    safetyEarned = Math.min(safetyEarned, safetyTotal);
    techniqueEarned = Math.min(techniqueEarned, techniqueTotal);

    const totalEarned = infectionEarned + dignityEarned + safetyEarned + techniqueEarned;

    return {
      totalEarned,
      isPassing: totalEarned >= 80 && !triggeredDisqualification,
      pillars: {
        infectionControl: { earned: infectionEarned, total: infectionTotal },
        residentDignity: { earned: dignityEarned, total: dignityTotal },
        safetyMechanics: { earned: safetyEarned, total: safetyTotal },
        proceduralTechnique: { earned: techniqueEarned, total: techniqueTotal }
      }
    };
  };

  const { totalEarned, isPassing, pillars } = calculateScores();

  // Execute an action
  const handleExecuteAction = (action: SimulatorHotspotAction) => {
    sound('click');

    // 1. Check instant fail disqualification
    if (action.disqualifiesIfTriggered) {
      sound('fail');
      setTriggeredDisqualification({
        id: action.actionId,
        name: action.label,
        triggerCondition: 'Direct student action',
        boardRationale: action.disqualificationReason || 'State Board mandatory clinical disqualification triggered.'
      });
      return;
    }

    // 2. Check instant fail traps in scenario
    if (scenario.instantFailTraps && scenario.instantFailTraps.length > 0) {
      const matchedTrap = scenario.instantFailTraps.find(trap => 
        trap.triggerCondition.toLowerCase() === action.actionId.toLowerCase() ||
        trap.id.toLowerCase() === action.actionId.toLowerCase()
      );
      if (matchedTrap) {
        sound('fail');
        setTriggeredDisqualification(matchedTrap);
        return;
      }
    }

    // 3. Apply state transitions
    const nextSimState = { ...simState };
    if (action.stateTransitions) {
      if (action.stateTransitions.handsClean !== undefined) nextSimState.handsClean = action.stateTransitions.handsClean;
      if (action.stateTransitions.curtainDrawn !== undefined) nextSimState.curtainDrawn = action.stateTransitions.curtainDrawn;
      if (action.stateTransitions.bedLocked !== undefined) nextSimState.bedLocked = action.stateTransitions.bedLocked;
      if (action.stateTransitions.bedHeight !== undefined) nextSimState.bedHeight = action.stateTransitions.bedHeight;
      if (action.stateTransitions.glovesOn !== undefined) nextSimState.glovesOn = action.stateTransitions.glovesOn;
      if (action.stateTransitions.nonSkidShoesOn !== undefined) nextSimState.nonSkidShoesOn = action.stateTransitions.nonSkidShoesOn;
      if (action.stateTransitions.callLightInReach !== undefined) nextSimState.callLightInReach = action.stateTransitions.callLightInReach;
      if (action.stateTransitions.gaitBeltApplied !== undefined) nextSimState.gaitBeltApplied = action.stateTransitions.gaitBeltApplied;
    }

    // Specific tool side effects
    if (action.actionId.includes('id') || action.actionId.includes('wristband')) {
      setIdBandVerified(true);
    }
    if (action.actionId.includes('fowler')) {
      setFowlersAngle(prev => prev === 'flat' ? 'semi' : prev === 'semi' ? 'high' : 'flat');
    }

    setSimState(nextSimState);
    setCompletedActionIds(prev => [...prev, action.actionId]);

    // Record action in log
    const nowStr = formatTime(secondsElapsed);
    setActionDialogueLogs(prev => [
      {
        text: action.feedbackDialogue || action.label,
        category: action.category,
        isCritical: action.isCritical,
        time: nowStr
      },
      ...prev
    ]);

    // Speech balloon trigger
    if (action.feedbackDialogue) {
      setResidentSpeech(action.feedbackDialogue);
      setTimeout(() => setResidentSpeech(null), 4500);
    }

    sound(action.isCritical ? 'success' : 'click');

    // Auto-advance phase if this phase milestones are completed
    const allCriticals = (scenario.hotspots || []).flatMap(h => (h.availableActions || []).filter(a => a.isCritical).map(a => a.actionId));
    const nextCompleted = [...completedActionIds, action.actionId];
    const completedCriticalCount = allCriticals.filter(id => nextCompleted.includes(id)).length;

    if (completedCriticalCount === allCriticals.length && allCriticals.length > 0) {
      setIsCompleted(true);
      setShowDebrief(true);
    }
  };

  // Determine which safety / state tags are tied to this specific simulator skill
  const activeTags = useMemo(() => {
    if (Array.isArray(scenario.activeSafetyTags) && scenario.activeSafetyTags.length > 0) {
      return scenario.activeSafetyTags;
    }
    const tags: Array<'bed_lock' | 'hands_clean' | 'call_light' | 'curtain' | 'shoes' | 'gloves'> = [];
    const allActions = (scenario.hotspots || []).flatMap(h => h.availableActions || []);
    const allActionText = allActions.map(a => `${a.actionId || ''} ${a.label || ''}`).join(' ').toLowerCase();
    const zones = new Set((scenario.hotspots || []).map(h => h.zone));
    const env = scenario.environment;

    // Hygiene: Relevant if sink zone exists OR actions mention wash/clean/hygiene/sanitize
    if (zones.has('sink') || /wash|hygiene|clean.*hand|hand.*clean|sanitize/i.test(allActionText) || allActions.some(a => a.category === 'hygiene')) {
      tags.push('hands_clean');
    }

    // Bed Brakes: ONLY if bed zone exists OR action explicitly mentions brake/lock bed/wheelchair, AND environment is not sink/phlebotomy
    if (env !== 'handwashing_station' && env !== 'phlebotomy_chair') {
      if (zones.has('bed') || /brake|lock.*bed|bed.*lock|wheelchair/i.test(allActionText)) {
        tags.push('bed_lock');
      }
    }

    // Call Light: ONLY if call_light zone exists OR action explicitly mentions call light
    if (zones.has('call_light') || /call.*light|signal.*light|call.*bell/i.test(allActionText)) {
      tags.push('call_light');
    }

    // Privacy Curtain: ONLY if privacy_curtain zone exists OR action mentions curtain/privacy, AND not handwashing
    if (env !== 'handwashing_station') {
      if (zones.has('privacy_curtain') || /curtain|privacy/i.test(allActionText)) {
        tags.push('curtain');
      }
    }

    // Footwear / Non-Skid Shoes: ONLY if actions mention shoe/footwear/sock/non-skid or skill title has ambulation/transfer
    if (/shoe|footwear|sock|non-skid/i.test(allActionText) || /ambulat|transfer|walk/i.test((scenario.title || '').toLowerCase())) {
      tags.push('shoes');
    }

    // Gloves: ONLY if action mentions glove or PPE or phlebotomy chair
    if (/glove|ppe|don.*glove/i.test(allActionText) || env === 'phlebotomy_chair') {
      tags.push('gloves');
    }

    return tags;
  }, [scenario.activeSafetyTags, scenario.hotspots, scenario.environment, scenario.title]);

  // Quick Direct Safety Action Toggles
  const handleQuickSafetyToggle = (type: 'bed_lock' | 'hands_clean' | 'call_light' | 'curtain' | 'shoes' | 'gloves') => {
    sound('latch');
    if (type === 'bed_lock') {
      const nextLocked = !simState.bedLocked;
      setSimState(prev => ({ ...prev, bedLocked: nextLocked }));
      const matchingAct = (scenario.hotspots || []).flatMap(h => h.availableActions || []).find(a => 
        a?.actionId?.includes('lock') || a?.actionId?.includes('brake')
      );
      if (matchingAct && !completedActionIds.includes(matchingAct.actionId)) {
        handleExecuteAction(matchingAct);
      } else {
        setActionDialogueLogs(prev => [
          { text: nextLocked ? "Bed caster wheel brakes verified LOCKED." : "Bed caster brakes unlocked.", category: 'safety', isCritical: true, time: formatTime(secondsElapsed) },
          ...prev
        ]);
      }
    } else if (type === 'hands_clean') {
      sound('water');
      const nextClean = !simState.handsClean;
      setSimState(prev => ({ ...prev, handsClean: nextClean }));
      const matchingAct = (scenario.hotspots || []).flatMap(h => h.availableActions || []).find(a => 
        a?.actionId?.includes('wash') || a?.category === 'hygiene'
      );
      if (matchingAct && !completedActionIds.includes(matchingAct.actionId)) {
        handleExecuteAction(matchingAct);
      } else {
        setActionDialogueLogs(prev => [
          { text: nextClean ? "20-second hand hygiene completed using soap and warm water with dry paper towel barrier." : "Hands need washing.", category: 'hygiene', isCritical: true, time: formatTime(secondsElapsed) },
          ...prev
        ]);
      }
    } else if (type === 'call_light') {
      sound('water');
      const nextCall = !simState.callLightInReach;
      setSimState(prev => ({ ...prev, callLightInReach: nextCall }));
      const matchingAct = (scenario.hotspots || []).flatMap(h => h.availableActions || []).find(a => 
        a?.actionId?.includes('call_light') || a?.actionId?.includes('call')
      );
      if (matchingAct && !completedActionIds.includes(matchingAct.actionId)) {
        handleExecuteAction(matchingAct);
      } else {
        setActionDialogueLogs(prev => [
          { text: nextCall ? "Emergency call light cord placed in resident's active hand." : "Call light docked on wall.", category: 'safety', isCritical: true, time: formatTime(secondsElapsed) },
          ...prev
        ]);
      }
    } else if (type === 'curtain') {
      const nextCurtain = !simState.curtainDrawn;
      setSimState(prev => ({ ...prev, curtainDrawn: nextCurtain }));
      const matchingAct = (scenario.hotspots || []).flatMap(h => h.availableActions || []).find(a => 
        a?.actionId?.includes('curtain') || a?.actionId?.includes('privacy')
      );
      if (matchingAct && !completedActionIds.includes(matchingAct.actionId)) {
        handleExecuteAction(matchingAct);
      } else {
        setActionDialogueLogs(prev => [
          { text: nextCurtain ? "Privacy curtain drawn around bed maintaining resident modesty." : "Privacy curtain opened.", category: 'dignity', isCritical: true, time: formatTime(secondsElapsed) },
          ...prev
        ]);
      }
    } else if (type === 'shoes') {
      const nextShoes = !simState.nonSkidShoesOn;
      setSimState(prev => ({ ...prev, nonSkidShoesOn: nextShoes }));
      const matchingAct = (scenario.hotspots || []).flatMap(h => h.availableActions || []).find(a => 
        a?.actionId?.includes('shoe') || a?.actionId?.includes('footwear')
      );
      if (matchingAct && !completedActionIds.includes(matchingAct.actionId)) {
        handleExecuteAction(matchingAct);
      } else {
        setActionDialogueLogs(prev => [
          { text: nextShoes ? "Resident non-skid safety footwear verified properly fitted." : "Resident footwear removed.", category: 'safety', isCritical: true, time: formatTime(secondsElapsed) },
          ...prev
        ]);
      }
    } else if (type === 'gloves') {
      const nextGloves = !simState.glovesOn;
      setSimState(prev => ({ ...prev, glovesOn: nextGloves }));
      const matchingAct = (scenario.hotspots || []).flatMap(h => h.availableActions || []).find(a => 
        a?.actionId?.includes('glove') || a?.actionId?.includes('ppe')
      );
      if (matchingAct && !completedActionIds.includes(matchingAct.actionId)) {
        handleExecuteAction(matchingAct);
      } else {
        setActionDialogueLogs(prev => [
          { text: nextGloves ? "Clean examination gloves donned according to aseptic precautions." : "Gloves removed.", category: 'hygiene', isCritical: true, time: formatTime(secondsElapsed) },
          ...prev
        ]);
      }
    }
  };

  // Download active photorealistic image for hosting on Wix CDN
  const handleDownloadImage = async () => {
    try {
      const response = await fetch(activeImageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photorealistic_${scenario.environment || 'room'}_${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      window.open(activeImageUrl, '_blank');
    }
  };

  // Apply custom Wix CDN or hosted image URL
  const handleApplyWixUrl = () => {
    const trimmed = wixUrlInput.trim();
    if (!onScenarioChange) return;
    onScenarioChange({
      ...scenario,
      customImageUrl: trimmed,
      imageUrl: trimmed
    });
    sound('success');
  };

  // Revert back to built-in environmental preset
  const handleClearCustomUrl = () => {
    setWixUrlInput('');
    if (!onScenarioChange) return;
    onScenarioChange({
      ...scenario,
      customImageUrl: '',
      imageUrl: ''
    });
    sound('click');
  };

  // Upload local image directly
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadStatus('Uploading image...');
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result as string;
        const res = await fetch('/practice-exams/api/clinical-sim/upload-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ base64Data, fileName: file.name })
        });
        const data = await res.json();
        if (data.url && onScenarioChange) {
          onScenarioChange({
            ...scenario,
            customImageUrl: data.url,
            imageUrl: data.url
          });
          setWixUrlInput(data.url);
          setUploadStatus('Uploaded successfully!');
          setTimeout(() => setUploadStatus(null), 3000);
          sound('success');
        }
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setUploadStatus('Upload failed');
    }
  };

  // Open AI modal with smart defaults
  const handleOpenAiImageModal = () => {
    const isHandwash = scenario.title.toLowerCase().includes('handwash') || 
                       scenario.title.toLowerCase().includes('hygiene') ||
                       scenario.environment === 'handwashing_station';
    const targetEnv = isHandwash ? 'handwashing_station' : (scenario.environment || 'bedside_room');
    setAiEnvironment(targetEnv);
    const initialFixtures = CLINICAL_SKILL_FIXTURES
      .filter(f => f.defaultForEnvs.includes(targetEnv))
      .map(f => f.id);
    setSelectedAiComponents(initialFixtures);
    setAiPrompt(buildSmartClinicalPrompt(targetEnv, scenario.title, initialFixtures, is360PanoramaMode, true));
    setIsAiImageModalOpen(true);
  };

  const handleAiEnvironmentChange = (newEnv: string) => {
    setAiEnvironment(newEnv);
    const newFixtures = CLINICAL_SKILL_FIXTURES
      .filter(f => f.defaultForEnvs.includes(newEnv))
      .map(f => f.id);
    setSelectedAiComponents(newFixtures);
    setAiPrompt(buildSmartClinicalPrompt(newEnv, scenario.title, newFixtures, is360PanoramaMode, true));
  };

  const handleToggleAiComponent = (fixtureId: string) => {
    const nextComponents = selectedAiComponents.includes(fixtureId)
      ? selectedAiComponents.filter(id => id !== fixtureId)
      : [...selectedAiComponents, fixtureId];
    setSelectedAiComponents(nextComponents);
    const hasCandidate = nextComponents.includes('candidate');
    setAiPrompt(buildSmartClinicalPrompt(aiEnvironment, scenario.title, nextComponents, is360PanoramaMode, hasCandidate));
  };

  const applyComponentPreset = (presetKey: 'nurse_patient' | 'ambulation' | 'vitals' | 'hygiene' | 'all' | 'none') => {
    let components: string[] = [];
    let studentActor = true;
    if (presetKey === 'nurse_patient') {
      components = ['candidate', 'resident', 'bed', 'overbed_table', 'call_light', 'privacy_curtain', 'sink'];
      studentActor = true;
    } else if (presetKey === 'ambulation') {
      components = ['candidate', 'resident', 'bed', 'wheelchair', 'call_light', 'overbed_table', 'sink'];
      studentActor = true;
    } else if (presetKey === 'vitals') {
      components = ['candidate', 'resident', 'bed', 'vitals_cuff', 'overbed_table', 'call_light'];
      studentActor = true;
    } else if (presetKey === 'hygiene') {
      components = ['candidate', 'sink', 'faucet', 'soap', 'towels', 'waste_bin', 'hygiene_poster'];
      studentActor = true;
    } else if (presetKey === 'all') {
      components = CLINICAL_SKILL_FIXTURES.map(f => f.id);
      studentActor = true;
    } else if (presetKey === 'none') {
      components = [];
      studentActor = false;
    }
    setSelectedAiComponents(components);
    const updatedPrompt = buildSmartClinicalPrompt(
      aiEnvironment,
      scenario.title,
      components,
      is360PanoramaMode,
      studentActor
    );
    setAiPrompt(updatedPrompt);
  };

  // Generate Photorealistic Image with AI
  const handleGenerateAiImage = async () => {
    try {
      setIsGeneratingAiImage(true);
      const activeComponentLabels = CLINICAL_SKILL_FIXTURES
        .filter(f => selectedAiComponents.includes(f.id))
        .map(f => f.label);

      const res = await fetch('/practice-exams/api/clinical-sim/generate-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environment: aiEnvironment,
          prompt: aiPrompt || undefined,
          scenarioTitle: scenario.title,
          skillName: scenario.title,
          components: activeComponentLabels,
          isPhotosphere3D: is360PanoramaMode,
          includeStudentActor: selectedAiComponents.includes('candidate')
        })
      });
      const data = await res.json();
      if (data.url) {
        setGeneratedAiImageUrl(data.dataUrl || data.url);
        sound('success');
      }
    } catch (err) {
      console.error('Image gen failed:', err);
    } finally {
      setIsGeneratingAiImage(false);
    }
  };

  // Apply AI synthesized image to the scenario
  const handleApplyGeneratedImage = () => {
    if (!generatedAiImageUrl || !onScenarioChange) return;
    if (is360PanoramaMode) {
      onScenarioChange({
        ...scenario,
        environment: aiEnvironment as any,
        photosphereUrl: generatedAiImageUrl,
        enable3dPhotosphere: true
      });
      setStageViewMode('photosphere');
    } else {
      onScenarioChange({
        ...scenario,
        environment: aiEnvironment as any,
        customImageUrl: generatedAiImageUrl,
        imageUrl: generatedAiImageUrl
      });
      setStageViewMode('photorealistic');
    }
    setWixUrlInput(generatedAiImageUrl);
    setIsAiImageModalOpen(false);
    sound('success');
  };

  // Nudge selected pin coordinates by delta
  const nudgeActivePin = (dx: number, dy: number) => {
    if (!calibratingHotspotId || !onScenarioChange) return;
    const updatedHotspots = scenario.hotspots.map(hs => {
      if (hs.id !== calibratingHotspotId) return hs;
      const current = hs.coordinates || envConfig.defaultHotspotCoordinates[hs.zone] || { x: 50, y: 50 };
      const nextX = Math.max(2, Math.min(98, current.x + dx));
      const nextY = Math.max(2, Math.min(98, current.y + dy));
      return { ...hs, coordinates: { x: nextX, y: nextY } };
    });
    onScenarioChange({ ...scenario, hotspots: updatedHotspots });
  };

  // Image touch / click ripple effect and hotspot positioning
  const handleStageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageStageRef.current) return;
    const rect = imageStageRef.current.getBoundingClientRect();
    const x = Math.max(2, Math.min(98, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
    const y = Math.max(2, Math.min(98, Math.round(((e.clientY - rect.top) / rect.height) * 100)));

    if (isCalibratingHotspots && calibratingHotspotId && onScenarioChange) {
      const updatedHotspots = scenario.hotspots.map(hs => {
        if (hs.id !== calibratingHotspotId) return hs;
        return { ...hs, coordinates: { x, y } };
      });
      onScenarioChange({ ...scenario, hotspots: updatedHotspots });
      sound('latch');
      return;
    }

    const newRipple = { id: Date.now(), x, y };
    setTouchRipples(prev => [...prev.slice(-2), newRipple]);
    setTimeout(() => {
      setTouchRipples(prev => prev.filter(r => r.id !== newRipple.id));
    }, 800);
  };

  // Restart Simulation
  const handleRestart = () => {
    sound('click');
    setCompletedActionIds([]);
    setActionDialogueLogs([]);
    setResidentSpeech(null);
    setSecondsElapsed(0);
    setIsCompleted(false);
    setShowDebrief(false);
    setTriggeredDisqualification(null);
    setIdBandVerified(false);
    setFowlersAngle('semi');
    setSimState({
      curtainDrawn: scenario.initialState?.curtainDrawn || false,
      bedHeight: scenario.initialState?.bedHeight || 'low',
      bedLocked: scenario.initialState?.bedLocked || false,
      handsClean: scenario.initialState?.handsClean || false,
      glovesOn: scenario.initialState?.glovesOn || false,
      nonSkidShoesOn: scenario.initialState?.nonSkidShoesOn || false,
      callLightInReach: scenario.initialState?.callLightInReach || false,
      gaitBeltApplied: scenario.initialState?.gaitBeltApplied || false,
      residentComfort: scenario.initialState?.residentComfort || 90
    });
    setActiveHotspot(scenario.hotspots[0] || null);
  };

  // Super Admin: Auto Solve Sandbox
  const handleAdminAutoSolve = () => {
    const allActions = scenario.hotspots.flatMap(h => h.availableActions);
    const nonDisqualifying = allActions.filter(a => !a.disqualifiesIfTriggered).map(a => a.actionId);
    setCompletedActionIds(nonDisqualifying);
    setSimState({
      curtainDrawn: true,
      bedHeight: 'low',
      bedLocked: true,
      handsClean: true,
      glovesOn: true,
      nonSkidShoesOn: true,
      callLightInReach: true,
      gaitBeltApplied: true,
      residentComfort: 100
    });
    setIdBandVerified(true);
    setIsCompleted(true);
    setShowDebrief(true);
    sound('success');
  };

  // Hotspot Icon Resolver
  const getHotspotIcon = (zone: SimulatorHotspot['zone']) => {
    switch (zone) {
      case 'sink': return Droplets;
      case 'bed': return BedDouble;
      case 'resident': return User;
      case 'call_light': return Bell;
      case 'supplies':
      case 'overbed_table': return Box;
      case 'privacy_curtain': return Shield;
      case 'waste_hamper': return Trash2;
      default: return MapPin;
    }
  };

  return (
    <div id="clinical-simulator-stage" className="relative w-full max-w-7xl mx-auto flex flex-col gap-5">
      {/* Super Admin Sandbox Bar */}
      {isAdminTestingMode && (
        <div id="admin-sandbox-banner" className="flex items-center justify-between p-3.5 bg-amber-500/10 border border-amber-400 dark:border-amber-700 rounded-xl text-amber-900 dark:text-amber-200 text-xs sm:text-sm font-semibold">
          <div className="flex items-center gap-2.5">
            <span className="flex h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse" />
            <span>SUPER ADMIN TEST SANDBOX • Clinical Simulation Engine</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAdminAutoSolve}
              className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-all shadow-xs"
            >
              Verify Happy Path (100% Pass)
            </button>
            <button
              type="button"
              onClick={handleRestart}
              className="px-2.5 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-100"
            >
              Reset State
            </button>
          </div>
        </div>
      )}

      {/* Clinical Telemetry HUD */}
      <div id="sim-telemetry-hud" className={`${theme.cardBg} p-4 sm:p-5 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4 shadow-sm`}>
        {/* Patient Profile */}
        {(() => {
          const safePatient = scenario?.patient || {
            name: 'Resident / Patient',
            roomNumber: '101',
            age: 72,
            primaryDiagnosis: 'Clinical Examination',
            mobilityStatus: 'Assisted',
            precautions: ['Standard Precautions'],
            baselineVitals: { bp: '120/80', hr: 72, rr: 16, spo2: 98, temp: '98.6°F' }
          };
          const vitals = safePatient.baselineVitals || { bp: '120/80', hr: 72, rr: 16, spo2: 98, temp: '98.6°F' };
          const precautions = Array.isArray(safePatient.precautions) ? safePatient.precautions : [];

          return (
            <>
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-slate-200 font-bold text-lg border border-slate-200 dark:border-slate-700 shadow-xs shrink-0">
                  <User className="w-6 h-6 text-slate-600 dark:text-slate-300" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 ${theme.headerFont}`}>
                      {safePatient.name}
                    </h3>
                    <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-semibold border border-slate-200 dark:border-slate-700">
                      Rm {safePatient.roomNumber}
                    </span>
                    <span className="px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 text-xs font-semibold border border-blue-200 dark:border-blue-800">
                      Age {safePatient.age}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 line-clamp-1">
                    Diagnosis: {safePatient.primaryDiagnosis} • {safePatient.mobilityStatus}
                  </p>
                </div>
              </div>

              {/* Live Vitals & Precautions Badges */}
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
                  <Heart className="w-3.5 h-3.5 text-rose-500" />
                  <span className="font-semibold text-slate-700 dark:text-slate-300">HR: {vitals.hr}</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
                  <Gauge className="w-3.5 h-3.5 text-blue-500" />
                  <span className="font-semibold text-slate-700 dark:text-slate-300">BP: {vitals.bp}</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
                  <Activity className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="font-semibold text-slate-700 dark:text-slate-300">SpO2: {vitals.spo2}%</span>
                </div>
                {precautions.map((prec, i) => (
                  <span key={i} className="px-2 py-1 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-300 font-semibold border border-amber-300 dark:border-amber-700 text-[11px]">
                    ⚠️ {prec}
                  </span>
                ))}
              </div>
            </>
          );
        })()}

        {/* Timer, Score, Sound Controls */}
        <div className="flex flex-wrap items-center gap-2.5 pt-2 lg:pt-0 border-t lg:border-t-0 border-slate-200 dark:border-slate-800 justify-between lg:justify-end">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 font-mono text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
              <Clock className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-slate-500" />
              <span>{formatTime(secondsElapsed)}</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
              <Award className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-amber-500" />
              <span>{totalEarned}/100 pts</span>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              id="toggle-sim-audio-btn"
              type="button"
              onClick={() => setAudioEnabled(!audioEnabled)}
              title={audioEnabled ? "Mute audio" : "Enable audio"}
              className="p-2 rounded-xl text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center cursor-pointer"
            >
              {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              id="sim-restart-btn"
              type="button"
              onClick={handleRestart}
              title="Restart Simulation"
              className="p-2 rounded-xl text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center cursor-pointer"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            {(user?.role === 'super_admin' || user?.role === 'admin' || isAdminTestingMode) && (
              <button
                id="sim-customize-studio-btn"
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                    window.speechSynthesis.cancel();
                  }
                  if (onExit) {
                    onExit();
                  } else {
                    navigate(`/manage/admin-clinical-simulators?certId=${encodeURIComponent(scenario.certId)}&scenarioId=${encodeURIComponent(scenario.id)}`);
                  }
                }}
                title={isAdminTestingMode ? "Exit Sandbox & Return to Authoring Studio" : "Customize this Scenario in Studio"}
                className="px-2.5 py-1.5 text-xs font-bold rounded-xl bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-800 hover:bg-purple-200 dark:hover:bg-purple-900/60 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer min-h-[36px]"
              >
                <Sliders className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                <span className="hidden sm:inline">{isAdminTestingMode ? "Clinical Sim Studio (Exit)" : "Clinical Sim Studio"}</span>
              </button>
            )}
            {onExit && (
              <button
                id="sim-exit-btn"
                type="button"
                onClick={handleExitSimulator}
                className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors min-h-[36px] flex items-center justify-center cursor-pointer"
              >
                Exit Lab
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Procedural Stepper Bar (Grid / Step Alignment) */}
      <div id="sim-procedural-stepper" className={`${theme.cardBg} p-2.5 sm:p-4 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3`}>
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 md:pb-0 flex-1 scrollbar-none touch-pan-x">
          {proceduralPhases.map((phase, idx) => {
            const isCurrent = activePhaseIndex === idx;
            // Check if actions matching these zones are done
            const phaseHotspots = scenario.hotspots.filter(h => phase.zones.includes(h.zone));
            const phaseActions = phaseHotspots.flatMap(h => h.availableActions);
            const doneInPhase = phaseActions.filter(a => completedActionIds.includes(a.actionId)).length;
            const isPhaseComplete = phaseActions.length > 0 && doneInPhase === phaseActions.length;

            return (
              <button
                key={phase.id}
                type="button"
                onClick={() => {
                  setActivePhaseIndex(idx);
                  const firstHotspot = phaseHotspots[0];
                  if (firstHotspot) setActiveHotspot(firstHotspot);
                  sound('click');
                }}
                className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 rounded-xl text-xs font-bold transition-all shrink-0 border min-h-[36px] ${
                  isCurrent
                    ? `${theme.activeTab} border-transparent shadow-xs`
                    : isPhaseComplete
                    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800'
                    : 'bg-slate-50 dark:bg-slate-800/70 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100'
                }`}
              >
                {isPhaseComplete ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                ) : (
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${isCurrent ? 'bg-white/20 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'}`}>
                    {idx + 1}
                  </span>
                )}
                <span className="whitespace-nowrap">{phase.name}</span>
              </button>
            );
          })}
        </div>

        {/* View Mode Toggle: 360° Photosphere vs Interactive Photo Room vs Step Cards Grid */}
        <div className="flex flex-wrap items-center bg-slate-100 dark:bg-slate-800 p-0.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs shrink-0 w-full sm:w-auto justify-center sm:justify-end gap-0.5">
          {/* 360° 3D VR Room (Available for all rooms with photospheres) */}
          {(is360Available || isAdminTestingMode) && (
            <button
              type="button"
              id="view-mode-photosphere-btn"
              onClick={() => {
                setStageViewMode('photosphere');
                sound('click');
              }}
              className={`px-2.5 sm:px-3 py-1.5 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer min-h-[32px] ${
                stageViewMode === 'photosphere'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>360° 3D Room</span>
            </button>
          )}

          <button
            type="button"
            id="view-mode-interactive-btn"
            onClick={() => {
              setStageViewMode('photorealistic');
              sound('click');
            }}
            className={`px-2.5 sm:px-3 py-1.5 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer min-h-[32px] ${
              stageViewMode === 'photorealistic'
                ? `${theme.activeTab} shadow-xs`
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            <ImageIcon className="w-3.5 h-3.5" />
            <span>Interactive Room</span>
          </button>
          <button
            type="button"
            id="view-mode-grid-btn"
            onClick={() => {
              setStageViewMode('grid');
              sound('click');
            }}
            className={`px-2.5 sm:px-3 py-1.5 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer min-h-[32px] ${
              stageViewMode === 'grid'
                ? `${theme.activeTab} shadow-xs`
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            <ListChecks className="w-3.5 h-3.5" />
            <span>Step Cards Grid</span>
          </button>
        </div>
      </div>

      {/* Disqualification Trap Alert (If triggered) */}
      {triggeredDisqualification && (
        <div id="sim-disqualification-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-xs">
          <div className="w-full max-w-lg bg-white dark:bg-slate-900 border-2 border-rose-500 rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in-95">
            <div className="w-14 h-14 rounded-2xl bg-rose-500/10 text-rose-600 flex items-center justify-center mx-auto mb-4 border border-rose-200 dark:border-rose-900">
              <ShieldAlert className="w-8 h-8" />
            </div>
            <span className="block text-center text-xs font-bold uppercase tracking-wider text-rose-600 mb-1">
              State Board Evaluator Alert
            </span>
            <h2 className="text-xl font-extrabold text-center text-slate-900 dark:text-slate-100 mb-2">
              Critical Disqualification Triggered
            </h2>
            <p className="text-sm font-semibold text-rose-700 dark:text-rose-400 text-center mb-4">
              "{triggeredDisqualification.name}"
            </p>

            <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-6">
              <p className="font-bold text-rose-900 dark:text-rose-200 mb-1">Official Board Standard:</p>
              {triggeredDisqualification.boardRationale}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleRestart}
                className="flex-1 py-3 px-4 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm transition-all shadow-md active:scale-95 text-center"
              >
                Reset & Try Again
              </button>
              {onExit && (
                <button
                  type="button"
                  onClick={handleExitSimulator}
                  className="py-3 px-4 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  Return to Study Mode
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Workspace: Room Stage & Action Inspector */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column (8 cols): Photo Stage OR Steps Grid */}
        <div className="lg:col-span-8 flex flex-col gap-4">
          {/* SUPER ADMIN SANDBOX TOOLBAR (Photorealistic Imagery & Hotspot Calibrator) */}
          {isAdminTestingMode && (
            <div id="super-admin-sandbox-toolbar" className="p-3.5 rounded-2xl bg-gradient-to-r from-purple-900/95 via-slate-900/95 to-blue-900/95 text-white shadow-lg border border-purple-500/30 flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2.5">
                <div className="flex items-center gap-2">
                  <div className="px-2 py-0.5 rounded-md bg-purple-500/30 border border-purple-400/50 text-[11px] font-mono font-bold text-purple-200">
                    SUPER ADMIN
                  </div>
                  <span className="text-xs font-semibold text-slate-200">
                    Interactive Hotspot Overlay & Photorealistic Imagery
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {/* Super Admin 360° Photosphere Enable/Disable for this skill */}
                  <button
                    id="admin-toggle-3d-photosphere-btn"
                    type="button"
                    onClick={() => {
                      const next = !scenario.enable3dPhotosphere;
                      if (onScenarioChange) {
                        onScenarioChange({ ...scenario, enable3dPhotosphere: next });
                      }
                      if (next) setStageViewMode('photosphere');
                      sound('click');
                    }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                      scenario.enable3dPhotosphere
                        ? 'bg-purple-500 text-white ring-2 ring-white shadow-md'
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                    }`}
                    title="Enable or disable 360° 3D Photosphere mode for this clinical skill"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>3D Room: {scenario.enable3dPhotosphere ? 'Enabled' : 'Disabled'}</span>
                  </button>

                  {/* Toggle Hotspot Overlay Calibration */}
                  <button
                    id="toggle-overlay-customizer-btn"
                    type="button"
                    onClick={() => {
                      const next = !isCalibratingHotspots;
                      setIsCalibratingHotspots(next);
                      if (next && scenario.hotspots.length > 0) {
                        setCalibratingHotspotId(scenario.hotspots[0].id);
                      }
                      sound('click');
                    }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                      isCalibratingHotspots
                        ? 'bg-amber-400 text-slate-950 ring-2 ring-white shadow-md'
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                    }`}
                  >
                    <Crosshair className="w-3.5 h-3.5" />
                    <span>{isCalibratingHotspots ? 'Done Customizing Overlay' : 'Customize Hotspot Overlay'}</span>
                  </button>

                  {(scenario.environment === 'handwashing_station' || scenario.title.toLowerCase().includes('handwash')) && onScenarioChange && (
                    <button
                      type="button"
                      onClick={() => {
                        onScenarioChange({
                          ...scenario,
                          environment: 'handwashing_station',
                          customImageUrl: scenario.customImageUrl || '/images/environments/handwashing_station.jpg',
                          imageUrl: scenario.imageUrl || '/images/environments/handwashing_station.jpg',
                          hotspots: HANDWASHING_HOTSPOTS_PRESET
                        });
                        sound('success');
                      }}
                      className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                      title="Auto-align station pins to the state-board standard sink, faucet, soap, towels, and bin"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-blue-200" />
                      <span>Auto-Align Handwashing Pins</span>
                    </button>
                  )}

                  {/* Generate Photorealistic Image with AI */}
                  <button
                    id="admin-generate-ai-img-btn"
                    type="button"
                    onClick={() => {
                      handleOpenAiImageModal();
                      sound('click');
                    }}
                    className="px-3 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-purple-200" />
                    <span>Generate Photorealistic Image (AI)</span>
                  </button>

                  {/* Download Image for Wix CDN */}
                  <button
                    id="admin-download-img-btn"
                    type="button"
                    onClick={handleDownloadImage}
                    title="Download active photorealistic image to host on your Wix CDN"
                    className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download Image for Wix CDN</span>
                  </button>
                </div>
              </div>

              {/* Custom Wix CDN or Hosted Image URL bar */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t border-white/10 text-xs">
                <div className="flex items-center gap-1.5 text-slate-300 font-semibold shrink-0">
                  <Globe className="w-3.5 h-3.5 text-blue-400" />
                  <span>Wix CDN / Hosted URL:</span>
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <input
                    id="sandbox-wix-cdn-url-input"
                    type="text"
                    value={wixUrlInput}
                    onChange={(e) => setWixUrlInput(e.target.value)}
                    placeholder="Paste Wix CDN URL (e.g., https://static.wixstatic.com/media/...) or preset path"
                    className="flex-1 px-3 py-1.5 rounded-xl bg-slate-900/90 border border-white/20 text-white text-xs placeholder:text-slate-500 focus:outline-hidden focus:border-blue-400 font-mono"
                  />
                  <button
                    id="apply-wix-cdn-url-btn"
                    type="button"
                    onClick={handleApplyWixUrl}
                    className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shrink-0 transition-colors cursor-pointer"
                  >
                    Apply URL
                  </button>
                  {(scenario.customImageUrl || scenario.imageUrl) && (
                    <button
                      id="clear-custom-img-url-btn"
                      type="button"
                      onClick={handleClearCustomUrl}
                      className="px-2.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white text-xs font-medium shrink-0 cursor-pointer"
                    >
                      Revert to Preset
                    </button>
                  )}
                </div>

                {/* Upload Local Image */}
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-2.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-slate-200 text-xs font-semibold flex items-center gap-1.5 cursor-pointer"
                    title="Upload image from computer"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span>Upload File</span>
                  </button>
                  {uploadStatus && (
                    <span className="text-[11px] text-amber-300 font-medium">{uploadStatus}</span>
                  )}
                </div>
              </div>

              {/* Hotspot Overlay Calibration Controls (when active) */}
              {isCalibratingHotspots && (
                <div className="p-3 rounded-xl bg-slate-900/90 border border-amber-400/40 flex flex-col gap-2.5 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                      <span className="font-bold text-amber-300">Overlay Customization Mode:</span>
                      <span className="text-slate-300">
                        Click a station pin, then <strong>click anywhere on the room image</strong> to position it!
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!onScenarioChange) return;
                          const newHs: SimulatorHotspot = {
                            id: `spot_${Date.now()}`,
                            name: 'New Clinical Fixture',
                            zone: 'supplies',
                            requiredPhase: 'any',
                            coordinates: { x: 50, y: 50 },
                            availableActions: [
                              {
                                actionId: `act_${Date.now()}`,
                                label: 'Perform Clinical Procedure',
                                feedbackDialogue: 'Candidate executed action at this station.',
                                isCritical: false,
                                pointsAwarded: 10,
                                category: 'technique'
                              }
                            ]
                          };
                          onScenarioChange({
                            ...scenario,
                            hotspots: [...scenario.hotspots, newHs]
                          });
                          setCalibratingHotspotId(newHs.id);
                          sound('click');
                        }}
                        className="px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center gap-1 cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>Add Station Pin</span>
                      </button>
                    </div>
                  </div>

                  {/* Hotspot station pill selector */}
                  <div className="flex items-center gap-2 overflow-x-auto pb-1">
                    {scenario.hotspots.map((hs) => {
                      const isCalActive = calibratingHotspotId === hs.id;
                      const coords = hs.coordinates || envConfig.defaultHotspotCoordinates[hs.zone] || { x: 50, y: 50 };
                      return (
                        <button
                          key={hs.id}
                          type="button"
                          onClick={() => {
                            setCalibratingHotspotId(hs.id);
                            sound('click');
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 shrink-0 transition-all cursor-pointer ${
                            isCalActive
                              ? 'bg-amber-400 text-slate-950 ring-2 ring-white shadow-md'
                              : 'bg-white/10 hover:bg-white/20 text-slate-200'
                          }`}
                        >
                          <span>{hs.name}</span>
                          <span className="text-[10px] font-mono opacity-80">({coords.x}%, {coords.y}%)</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Fine-tune directional stepper for selected pin */}
                  {calibratingHotspotId && (
                    <div className="flex items-center justify-between gap-3 pt-1 border-t border-white/10 text-xs flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-300 font-semibold">Fine-tune Pin Position:</span>
                        <div className="flex items-center gap-1 bg-black/40 p-1 rounded-lg border border-white/10">
                          <button
                            type="button"
                            onClick={() => nudgeActivePin(-1, 0)}
                            title="Move Left 1%"
                            className="p-1 rounded hover:bg-white/20 text-white cursor-pointer"
                          >
                            <ArrowLeft className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => nudgeActivePin(0, -1)}
                            title="Move Up 1%"
                            className="p-1 rounded hover:bg-white/20 text-white cursor-pointer"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => nudgeActivePin(0, 1)}
                            title="Move Down 1%"
                            className="p-1 rounded hover:bg-white/20 text-white cursor-pointer"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => nudgeActivePin(1, 0)}
                            title="Move Right 1%"
                            className="p-1 rounded hover:bg-white/20 text-white cursor-pointer"
                          >
                            <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          if (!onScenarioChange || !calibratingHotspotId) return;
                          const activeHs = scenario.hotspots.find(h => h.id === calibratingHotspotId);
                          if (!activeHs) return;
                          const defCoords = envConfig.defaultHotspotCoordinates[activeHs.zone] || { x: 50, y: 50 };
                          const updatedHotspots = scenario.hotspots.map(hs => {
                            if (hs.id !== calibratingHotspotId) return hs;
                            return { ...hs, coordinates: defCoords };
                          });
                          onScenarioChange({ ...scenario, hotspots: updatedHotspots });
                          sound('click');
                        }}
                        className="text-xs text-blue-400 hover:text-blue-300 underline font-semibold cursor-pointer"
                      >
                        Reset Selected Pin to Fixture Default
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {stageViewMode === 'photosphere' ? (
            /* Option A: Interactive 360° Equirectangular Photosphere Stage with 3D Projected Hotspots */
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between px-1 text-xs">
                <div className="flex items-center gap-2 font-mono font-bold text-slate-700 dark:text-slate-300 uppercase">
                  <span className="w-2.5 h-2.5 rounded-full bg-purple-500 animate-ping" />
                  <span>360° VR Photosphere • {envConfig.name}</span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                  <Info className="w-3.5 h-3.5" />
                  <span>
                    {isCalibratingHotspots 
                      ? 'Click anywhere on the 360° spherical room to reposition the active pin' 
                      : 'Drag mouse or finger to look around 360°. Click any station to begin steps.'}
                  </span>
                </div>
              </div>

              <ClinicalPhotosphereStage
                panoramaUrl={activePhotosphereUrl}
                fallbackImageUrl={activeImageUrl}
                hotspots={scenario.hotspots}
                activeHotspotId={activeHotspot?.id || null}
                onSelectHotspot={(hs) => {
                  setActiveHotspot(hs);
                  sound('click');
                }}
                isCalibrating={isCalibratingHotspots}
                calibratingHotspotId={calibratingHotspotId}
                onCalibrateHotspot={(hotspotId, spherical) => {
                  if (!onScenarioChange) return;
                  const updatedHotspots = scenario.hotspots.map(h => {
                    if (h.id !== hotspotId) return h;
                    return {
                      ...h,
                      sphericalCoordinates: spherical
                      // Keep 2D coordinates completely independent
                    };
                  });
                  onScenarioChange({ ...scenario, hotspots: updatedHotspots });
                  sound('success');
                }}
                onNudgePin={(dYaw, dPitch) => {
                  if (!onScenarioChange || !calibratingHotspotId) return;
                  const targetHs = scenario.hotspots.find(h => h.id === calibratingHotspotId);
                  if (!targetHs) return;
                  const currentSpherical = targetHs.sphericalCoordinates || coords2DtoSpherical(
                    targetHs.coordinates?.x ?? 50,
                    targetHs.coordinates?.y ?? 50
                  );
                  const newSpherical = {
                    yaw: Math.max(-180, Math.min(180, currentSpherical.yaw + dYaw)),
                    pitch: Math.max(-85, Math.min(85, currentSpherical.pitch + dPitch))
                  };
                  const updatedHotspots = scenario.hotspots.map(h => {
                    if (h.id !== calibratingHotspotId) return h;
                    return {
                      ...h,
                      sphericalCoordinates: newSpherical
                      // Keep 2D coordinates completely independent
                    };
                  });
                  onScenarioChange({ ...scenario, hotspots: updatedHotspots });
                  sound('click');
                }}
                environmentName={envConfig.name}
                completedActionIds={completedActionIds}
                skillTitle={scenario.title}
                onExecuteAction={(act) => handleExecuteAction(act)}
                autoStartTour={initialTourMode}
                onFallbackTo2D={() => {
                  setStageViewMode('photorealistic');
                }}
              />
            </div>
          ) : stageViewMode === 'photorealistic' ? (
            /* Interactive Clinical Hotspot Stage on High-Fidelity Environmental Imagery */
            <div className="flex flex-col gap-3">
              {/* Room Header Info */}
              <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs">
                <div className="flex items-center gap-2 font-mono font-bold text-slate-700 dark:text-slate-300 uppercase">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
                  <span>{envConfig.name}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {is360Available && (
                    <button
                      type="button"
                      onClick={() => {
                        setStageViewMode('photosphere');
                        sound('click');
                      }}
                      className="px-2.5 py-1 rounded-lg bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 hover:bg-purple-200 text-xs font-bold flex items-center gap-1.5 transition-all shadow-2xs cursor-pointer"
                      title="Switch to full 360° VR 3D Panoramic Stage"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                      <span>Switch to 360° 3D VR</span>
                    </button>
                  )}
                  <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                    <Info className="w-3.5 h-3.5" />
                    <span>{isCalibratingHotspots ? 'Click anywhere on the room image to reposition the active pin' : 'Click any highlighted station in the room to perform steps'}</span>
                  </div>
                </div>
              </div>

              {/* Photorealistic Interactive Canvas Stage */}
              <div 
                id="interactive-clinical-room-stage"
                ref={imageStageRef}
                onClick={handleStageClick}
                className="relative w-full rounded-2xl overflow-hidden shadow-xl border-2 border-slate-300 dark:border-slate-800 bg-slate-950 aspect-video select-none cursor-pointer group"
              >
                {/* 360° VR Quick Switch Badge on Canvas */}
                {is360Available && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStageViewMode('photosphere');
                      sound('click');
                    }}
                    className="absolute top-4 right-4 z-20 px-3 py-1.5 rounded-xl bg-purple-900/80 hover:bg-purple-800 text-white border border-purple-400/50 backdrop-blur-md shadow-lg flex items-center gap-2 text-xs font-bold transition-all hover:scale-105 active:scale-95 cursor-pointer"
                  >
                    <Sparkles className="w-4 h-4 text-purple-300 animate-pulse" />
                    <span>Enter 360° VR Mode</span>
                  </button>
                )}
                {/* Authentic High-Fidelity Room Image with fallback */}
                <img 
                  src={activeImageUrl} 
                  alt={envConfig.name}
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (target.src !== window.location.origin + '/images/environments/bedside_room.jpg') {
                      target.src = '/images/environments/bedside_room.jpg';
                    }
                  }}
                  className="w-full h-full object-cover object-center pointer-events-none transition-transform duration-700 group-hover:scale-[1.01]"
                />

                {/* Subtle Cinematic Vignette Overlay */}
                <div className="absolute inset-0 pointer-events-none bg-radial from-transparent via-black/10 to-black/40" />

                {/* Touch wave ripples */}
                {touchRipples.map(ripple => (
                  <span
                    key={ripple.id}
                    className="absolute pointer-events-none rounded-full border-2 border-blue-400 bg-blue-400/20 animate-ping z-30"
                    style={{
                      left: `${ripple.x}%`,
                      top: `${ripple.y}%`,
                      width: '40px',
                      height: '40px',
                      transform: 'translate(-50%, -50%)'
                    }}
                  />
                ))}

                {/* Floating Resident Dialogue Balloon */}
                {residentSpeech && (
                  <div className="absolute top-[22%] left-[40%] -translate-x-1/2 z-40 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-4 py-2.5 rounded-2xl shadow-2xl text-xs font-bold border-2 border-blue-400 max-w-sm animate-in fade-in zoom-in-95 pointer-events-none">
                    <div className="flex items-center gap-1.5 text-[10px] font-extrabold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-0.5">
                      <User className="w-3 h-3" />
                      <span>{scenario?.patient?.name || 'Resident'}</span>
                    </div>
                    <p className="leading-snug">{residentSpeech}</p>
                    <div className="w-3.5 h-3.5 bg-white dark:bg-slate-900 rotate-45 absolute -bottom-1.5 left-8 border-r-2 border-b-2 border-blue-400" />
                  </div>
                )}

                {/* Interactive Hotspot Radar Pins Overlaid on Real Room Equipment */}
                {(scenario?.hotspots || []).map((hotspot) => {
                  const coords = getHotspotCoordinates(hotspot);
                  const isSelected = activeHotspot?.id === hotspot.id;
                  const isCalibrating = isCalibratingHotspots && calibratingHotspotId === hotspot.id;
                  const totalActions = hotspot.availableActions.length;
                  const completedCount = hotspot.availableActions.filter(a => completedActionIds.includes(a.actionId)).length;
                  const isAllDone = totalActions > 0 && completedCount === totalActions;
                  const IconComp = getHotspotIcon(hotspot.zone);

                  return (
                    <div
                      key={hotspot.id}
                      style={{
                        position: 'absolute',
                        left: `${coords.x}%`,
                        top: `${coords.y}%`,
                        transform: 'translate(-50%, -50%)',
                        zIndex: isCalibrating ? 40 : isSelected ? 30 : 20
                      }}
                      className="cursor-pointer"
                    >
                      {/* Interactive Pin Button */}
                      <button
                        type="button"
                        id={`room-pin-${hotspot.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isCalibratingHotspots) {
                            setCalibratingHotspotId(hotspot.id);
                            sound('click');
                          } else {
                            setActiveHotspot(hotspot);
                            sound('click');
                          }
                        }}
                        className="group/pin relative flex flex-col items-center focus:outline-hidden"
                      >
                        {/* Radar Pulse Ring */}
                        {!isAllDone && (
                          <span className={`absolute -inset-2.5 rounded-full animate-ping opacity-60 pointer-events-none ${isCalibrating ? 'bg-amber-400' : isSelected ? 'bg-blue-400' : 'bg-cyan-400'}`} />
                        )}

                        {/* Pin Circular Badge */}
                        <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 border-2 ${
                          isCalibrating
                            ? 'bg-amber-400 text-slate-950 border-white ring-4 ring-amber-300 scale-125'
                            : isAllDone
                            ? 'bg-emerald-600 text-white border-emerald-300 ring-2 ring-emerald-500/40'
                            : isSelected
                            ? 'bg-blue-600 text-white border-white ring-4 ring-blue-500/50 scale-110'
                            : 'bg-white/95 dark:bg-slate-900/95 text-slate-800 dark:text-slate-100 border-blue-400 hover:scale-110 hover:border-white shadow-blue-500/30'
                        }`}>
                          {isAllDone ? (
                            <Check className="w-5 h-5 text-white" />
                          ) : (
                            <IconComp className={`w-5 h-5 ${isCalibrating ? 'text-slate-950 font-bold' : isSelected ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`} />
                          )}
                        </div>

                        {/* Pin Label Tooltip Banner */}
                        <div className={`mt-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider whitespace-nowrap shadow-lg backdrop-blur-md transition-all border ${
                          isCalibrating
                            ? 'bg-amber-400 text-slate-950 border-white ring-2 ring-amber-300'
                            : isSelected
                            ? 'bg-blue-600 text-white border-blue-400'
                            : isAllDone
                            ? 'bg-emerald-950/90 text-emerald-200 border-emerald-600/70'
                            : 'bg-slate-900/90 text-white border-slate-700/80 group-hover/pin:bg-slate-800'
                        }`}>
                          <span>{hotspot.name}</span>
                          {totalActions > 0 && !isCalibratingHotspots && (
                            <span className="ml-1 opacity-80 font-mono">
                              ({completedCount}/{totalActions})
                            </span>
                          )}
                        </div>

                        {/* Hotspot coordinate indicator during overlay calibration */}
                        {isCalibratingHotspots && (
                          <div className={`mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold shadow-xs whitespace-nowrap ${
                            calibratingHotspotId === hotspot.id
                              ? 'bg-amber-400 text-slate-950 ring-2 ring-white scale-110 z-40'
                              : 'bg-black/80 text-amber-200'
                          }`}>
                            {coords.x}%, {coords.y}%
                          </div>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Skill-Tied Safety Status & Precaution Tags Bar */}
              {activeTags.length > 0 && (
                <div className="flex flex-col gap-2 p-3 bg-slate-50 dark:bg-slate-900/90 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
                  <div className="flex items-center justify-between px-0.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />
                      <span>Skill Safety Precautions & Status Tags</span>
                    </span>
                    <span className="text-[10px] font-medium normal-case text-slate-500 dark:text-slate-400">
                      Tied strictly to: <strong className="text-slate-700 dark:text-slate-200">{scenario.title || 'Current Skill'}</strong>
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* 1. Bed Brakes Locked - Only when tied to skill */}
                    {activeTags.includes('bed_lock') && (
                      <button
                        type="button"
                        id="sim-tag-bed-lock"
                        onClick={() => handleQuickSafetyToggle('bed_lock')}
                        className={`p-2 rounded-lg border flex items-center justify-between gap-2.5 transition-all cursor-pointer shadow-2xs ${
                          simState.bedLocked 
                            ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 font-bold'
                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                        }`}
                        title="Toggle Bed Brakes Lock precaution"
                      >
                        <div className="flex items-center gap-1.5">
                          {simState.bedLocked ? <Lock className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <Unlock className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                          <span className="text-[11px]">Bed Brakes</span>
                        </div>
                        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${
                          simState.bedLocked ? 'bg-emerald-600 text-white font-bold' : 'bg-black/5 dark:bg-white/10 text-slate-500'
                        }`}>
                          {simState.bedLocked ? 'Locked' : 'Unlocked'}
                        </span>
                      </button>
                    )}

                    {/* 2. Hygiene (Hand Hygiene) - Only when tied to skill */}
                    {activeTags.includes('hands_clean') && (
                      <button
                        type="button"
                        id="sim-tag-hygiene"
                        onClick={() => handleQuickSafetyToggle('hands_clean')}
                        className={`p-2 rounded-lg border flex items-center justify-between gap-2.5 transition-all cursor-pointer shadow-2xs ${
                          simState.handsClean 
                            ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 font-bold'
                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                        }`}
                        title="Toggle Hand Hygiene clean state"
                      >
                        <div className="flex items-center gap-1.5">
                          <Droplets className={`w-3.5 h-3.5 shrink-0 ${simState.handsClean ? 'text-emerald-600' : 'text-slate-400'}`} />
                          <span className="text-[11px]">Hygiene</span>
                        </div>
                        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${
                          simState.handsClean ? 'bg-emerald-600 text-white font-bold' : 'bg-rose-100 text-rose-700 font-bold dark:bg-rose-950/60 dark:text-rose-300'
                        }`}>
                          {simState.handsClean ? 'Clean' : 'Needs Wash'}
                        </span>
                      </button>
                    )}

                    {/* 3. Call Light - Only when tied to skill */}
                    {activeTags.includes('call_light') && (
                      <button
                        type="button"
                        id="sim-tag-call-light"
                        onClick={() => handleQuickSafetyToggle('call_light')}
                        className={`p-2 rounded-lg border flex items-center justify-between gap-2.5 transition-all cursor-pointer shadow-2xs ${
                          simState.callLightInReach 
                            ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 font-bold'
                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                        }`}
                        title="Toggle Resident Call Light placement"
                      >
                        <div className="flex items-center gap-1.5">
                          <Bell className={`w-3.5 h-3.5 shrink-0 ${simState.callLightInReach ? 'text-emerald-600' : 'text-slate-400'}`} />
                          <span className="text-[11px]">Call Light</span>
                        </div>
                        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${
                          simState.callLightInReach ? 'bg-emerald-600 text-white font-bold' : 'bg-black/5 dark:bg-white/10 text-slate-500'
                        }`}>
                          {simState.callLightInReach ? 'In Hand' : 'On Wall'}
                        </span>
                      </button>
                    )}

                    {/* 4. Privacy Curtain - Only when tied to skill */}
                    {activeTags.includes('curtain') && (
                      <button
                        type="button"
                        id="sim-tag-curtain"
                        onClick={() => handleQuickSafetyToggle('curtain')}
                        className={`p-2 rounded-lg border flex items-center justify-between gap-2.5 transition-all cursor-pointer shadow-2xs ${
                          simState.curtainDrawn 
                            ? 'bg-purple-50 dark:bg-purple-950/40 border-purple-300 dark:border-purple-800 text-purple-800 dark:text-purple-200 font-bold'
                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                        }`}
                        title="Toggle Resident Privacy Curtain"
                      >
                        <div className="flex items-center gap-1.5">
                          <Shield className={`w-3.5 h-3.5 shrink-0 ${simState.curtainDrawn ? 'text-purple-600' : 'text-slate-400'}`} />
                          <span className="text-[11px]">Curtain</span>
                        </div>
                        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${
                          simState.curtainDrawn ? 'bg-purple-600 text-white font-bold' : 'bg-black/5 dark:bg-white/10 text-slate-500'
                        }`}>
                          {simState.curtainDrawn ? 'Drawn' : 'Open'}
                        </span>
                      </button>
                    )}

                    {/* 5. Footwear (Non-Skid Shoes) - Only when tied to skill */}
                    {activeTags.includes('shoes') && (
                      <button
                        type="button"
                        id="sim-tag-shoes"
                        onClick={() => handleQuickSafetyToggle('shoes')}
                        className={`p-2 rounded-lg border flex items-center justify-between gap-2.5 transition-all cursor-pointer shadow-2xs ${
                          simState.nonSkidShoesOn 
                            ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 font-bold'
                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                        }`}
                        title="Toggle Resident Non-Skid Footwear"
                      >
                        <div className="flex items-center gap-1.5">
                          <Footprints className={`w-3.5 h-3.5 shrink-0 ${simState.nonSkidShoesOn ? 'text-emerald-600' : 'text-slate-400'}`} />
                          <span className="text-[11px]">Footwear</span>
                        </div>
                        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${
                          simState.nonSkidShoesOn ? 'bg-emerald-600 text-white font-bold' : 'bg-amber-100 text-amber-800 font-bold dark:bg-amber-950/60 dark:text-amber-300'
                        }`}>
                          {simState.nonSkidShoesOn ? 'Non-Skid On' : 'Bare/None'}
                        </span>
                      </button>
                    )}

                    {/* 6. Exam Gloves - Only when tied to skill */}
                    {activeTags.includes('gloves') && (
                      <button
                        type="button"
                        id="sim-tag-gloves"
                        onClick={() => handleQuickSafetyToggle('gloves')}
                        className={`p-2 rounded-lg border flex items-center justify-between gap-2.5 transition-all cursor-pointer shadow-2xs ${
                          simState.glovesOn 
                            ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 font-bold'
                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                        }`}
                        title="Toggle PPE / Exam Gloves"
                      >
                        <div className="flex items-center gap-1.5">
                          <ShieldCheck className={`w-3.5 h-3.5 shrink-0 ${simState.glovesOn ? 'text-emerald-600' : 'text-slate-400'}`} />
                          <span className="text-[11px]">Exam Gloves</span>
                        </div>
                        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${
                          simState.glovesOn ? 'bg-emerald-600 text-white font-bold' : 'bg-black/5 dark:bg-white/10 text-slate-500'
                        }`}>
                          {simState.glovesOn ? 'Donned' : 'Off'}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Structured Step Cards Grid Mode (The view students love for systematic learning) */
            <div id="sim-step-cards-grid" className="flex flex-col gap-4">
              <div className="flex items-center justify-between px-1 text-xs">
                <span className="font-bold text-slate-700 dark:text-slate-300">
                  All Procedural Stations ({scenario.hotspots.length} Stations)
                </span>
                <span className="text-slate-500">
                  Click any station card to inspect and execute care steps
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {scenario.hotspots.map((hotspot) => {
                  const isSelected = activeHotspot?.id === hotspot.id;
                  const totalActions = hotspot.availableActions.length;
                  const completedInHotspot = hotspot.availableActions.filter(a => completedActionIds.includes(a.actionId)).length;
                  const isFullyDone = completedInHotspot === totalActions && totalActions > 0;
                  const IconComp = getHotspotIcon(hotspot.zone);

                  return (
                    <button
                      key={hotspot.id}
                      type="button"
                      onClick={() => {
                        setActiveHotspot(hotspot);
                        sound('click');
                      }}
                      className={`p-4 rounded-2xl text-left transition-all border flex flex-col justify-between min-h-[130px] shadow-xs cursor-pointer ${
                        isSelected
                          ? `${theme.cardBg} border-2 ${theme.accentBorder} shadow-md`
                          : isFullyDone
                          ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 hover:border-emerald-400'
                          : `${theme.cardBg} border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700`
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded-lg ${isFullyDone ? 'bg-emerald-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
                              <IconComp className="w-4 h-4" />
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                              {hotspot.zone.replace('_', ' ')}
                            </span>
                          </div>
                          {isFullyDone && (
                            <span className="px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200 text-[10px] font-bold flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Complete
                            </span>
                          )}
                        </div>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                          {hotspot.name}
                        </h4>
                      </div>

                      <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 text-[11px] font-mono text-slate-500">
                        <span>{totalActions} procedures</span>
                        <span className={isFullyDone ? "text-emerald-600 dark:text-emerald-400 font-bold" : ""}>
                          {completedInHotspot}/{totalActions} executed
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Examiner Observation Transcript Stream */}
          <div id="sim-dialogue-transcript" className={`${theme.cardBg} p-4 sm:p-5 shadow-xs`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className={`text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 ${theme.headerFont}`}>
                <Activity className="w-4 h-4 text-blue-500" />
                Examiner & Resident Observation Stream
              </h4>
              <span className="text-xs text-slate-500 font-mono">
                {actionDialogueLogs.length} events logged
              </span>
            </div>

            {actionDialogueLogs.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-500 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                No procedural actions logged yet. Select a station above to begin care.
              </div>
            ) : (
              <div className="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1">
                {actionDialogueLogs.map((log, idx) => (
                  <div 
                    key={idx} 
                    className={`p-2.5 rounded-lg text-xs flex items-start gap-2.5 border ${
                      log.isCritical 
                        ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/50 text-emerald-950 dark:text-emerald-200' 
                        : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <span className="font-mono text-[10px] text-slate-400 shrink-0 mt-0.5">{log.time}</span>
                    <div className="flex-1">
                      {log.isCritical && (
                        <span className="inline-block px-1.5 py-0.2 rounded text-[9px] font-extrabold uppercase tracking-wider bg-emerald-600 text-white mr-1.5">
                          Critical Milestone
                        </span>
                      )}
                      <span>{log.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column (4 cols): Active Station Inspector & Action Selection Sheet */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          <div id="sim-station-inspector" className={`${theme.cardBg} p-5 shadow-sm min-h-[480px] flex flex-col justify-between`}>
            {activeHotspot ? (
              <div>
                {/* Station Title Header */}
                <div className="flex items-center justify-between pb-3.5 border-b border-slate-200 dark:border-slate-800 mb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-900">
                      {React.createElement(getHotspotIcon(activeHotspot.zone), { className: "w-5 h-5" })}
                    </div>
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        {activeHotspot.zone.replace('_', ' ')} station
                      </span>
                      <h3 className={`text-base font-bold text-slate-900 dark:text-slate-100 ${theme.headerFont}`}>
                        {activeHotspot.name}
                      </h3>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveHotspot(null)}
                    className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>

                <p className="text-xs text-slate-600 dark:text-slate-400 mb-4">
                  Select clinical action to execute at this station:
                </p>

                {/* Available Actions List */}
                <div className="flex flex-col gap-2.5">
                  {activeHotspot.availableActions.map((action) => {
                    const isDone = completedActionIds.includes(action.actionId);

                    return (
                      <button
                        key={action.actionId}
                        id={`action-btn-${action.actionId}`}
                        type="button"
                        onClick={() => handleExecuteAction(action)}
                        disabled={isDone}
                        className={`w-full p-3.5 rounded-xl text-left transition-all border flex flex-col gap-1.5 cursor-pointer ${
                          isDone
                            ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 opacity-80 cursor-default'
                            : 'bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 hover:border-blue-500 hover:bg-blue-50/40 dark:hover:bg-blue-950/30 active:scale-[0.99] shadow-2xs'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-bold text-slate-900 dark:text-slate-100 leading-snug">
                            {action.label}
                          </span>
                          {isDone ? (
                            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                          ) : (
                            <span className="text-[10px] font-mono text-slate-500 shrink-0">
                              +{action.pointsAwarded} pts
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5 flex-wrap">
                          {action.isCritical && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
                              Critical Step
                            </span>
                          )}
                          <span className="text-[10px] text-slate-500 capitalize">
                            Category: {action.category}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-slate-500">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-3 border border-blue-200 dark:border-blue-800">
                  <Play className="w-6 h-6 ml-0.5" />
                </div>
                <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">
                  No Station Selected
                </h4>
                <p className="text-xs text-slate-600 dark:text-slate-400 max-w-xs">
                  Click any hotspot radar pin on the room image (Sink, Bed, Call Light, Table, Resident) to view and execute procedural actions.
                </p>
              </div>
            )}

            {/* Submit Simulation Action Button */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-800 mt-4">
              <button
                id="submit-sim-debrief-btn"
                type="button"
                onClick={() => {
                  setIsCompleted(true);
                  setShowDebrief(true);
                  sound('success');
                }}
                className={`w-full py-3 px-4 ${theme.primaryBtn} flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer`}
              >
                <Award className="w-4 h-4" />
                Submit Simulation for Scoring
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Completion Debrief & Four-Pillar Scorecard Modal */}
      {showDebrief && (
        <div id="sim-debrief-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-xs">
          <div className="w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-7 shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Result Header */}
            <div className="text-center pb-5 border-b border-slate-200 dark:border-slate-800">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md ${
                isPassing 
                  ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-300 dark:border-emerald-800' 
                  : 'bg-amber-500/10 text-amber-600 border border-amber-300 dark:border-amber-800'
              }`}>
                {isPassing ? <Award className="w-9 h-9" /> : <AlertTriangle className="w-9 h-9" />}
              </div>
              <span className={`text-xs font-bold uppercase tracking-wider ${isPassing ? 'text-emerald-600' : 'text-amber-600'}`}>
                {isPassing ? 'State Board Benchmark Satisfied' : 'Remediation Recommended'}
              </span>
              <h2 className={`text-2xl font-black text-slate-900 dark:text-slate-100 mt-1 ${theme.headerFont}`}>
                {isPassing ? 'Clinical Simulation Passed!' : 'Simulation Incomplete'}
              </h2>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 max-w-md mx-auto">
                {isPassing 
                  ? `Candidate successfully demonstrated procedural mastery for ${scenario.title} within official state board guidelines.`
                  : 'Review the pillar breakdown below to identify required safety and procedural milestones that need reinforcement.'}
              </p>
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 font-bold text-sm text-slate-900 dark:text-slate-100">
                <span>Total Clinical Score:</span>
                <span className={isPassing ? "text-emerald-600" : "text-amber-600"}>{totalEarned} / 100</span>
                <span className="text-slate-400">• Time: {formatTime(secondsElapsed)}</span>
              </div>
            </div>

            {/* Four-Pillars Scoring Matrix */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 my-6">
              {/* Infection Control */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Infection Control</span>
                  <span className="font-mono text-xs font-bold text-blue-600 dark:text-blue-400">
                    {pillars.infectionControl.earned} / {pillars.infectionControl.total} pts
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${(pillars.infectionControl.earned / pillars.infectionControl.total) * 100}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-2">
                  Hand hygiene timing, barrier towel protocol, aseptic technique.
                </p>
              </div>

              {/* Resident Dignity */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Resident Dignity & Rights</span>
                  <span className="font-mono text-xs font-bold text-purple-600 dark:text-purple-400">
                    {pillars.residentDignity.earned} / {pillars.residentDignity.total} pts
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                  <div 
                    className="h-full bg-purple-500 rounded-full transition-all"
                    style={{ width: `${(pillars.residentDignity.earned / pillars.residentDignity.total) * 100}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-2">
                  Greeting by name, ID verification, privacy curtain, consent.
                </p>
              </div>

              {/* Safety & Body Mechanics */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Safety & Mechanics</span>
                  <span className="font-mono text-xs font-bold text-emerald-600 dark:text-emerald-400">
                    {pillars.safetyMechanics.earned} / {pillars.safetyMechanics.total} pts
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${(pillars.safetyMechanics.earned / pillars.safetyMechanics.total) * 100}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-2">
                  Bed brakes, non-skid footwear, transfer belt, call light placement.
                </p>
              </div>

              {/* Procedural Technique */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Procedural Technique</span>
                  <span className="font-mono text-xs font-bold text-amber-600 dark:text-amber-400">
                    {pillars.proceduralTechnique.earned} / {pillars.proceduralTechnique.total} pts
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full transition-all"
                    style={{ width: `${(pillars.proceduralTechnique.earned / pillars.proceduralTechnique.total) * 100}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-2">
                  Step execution accuracy, resident communication, clean completion.
                </p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 pt-3 border-t border-slate-200 dark:border-slate-800">
              <button
                type="button"
                onClick={handleRestart}
                className="flex-1 py-3 px-4 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 font-bold text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-center cursor-pointer"
              >
                Repeat Simulation Run
              </button>
              {onExit && (
                <button
                  type="button"
                  onClick={handleExitSimulator}
                  className={`flex-1 py-3 px-4 ${theme.primaryBtn} font-bold text-sm text-center shadow-md cursor-pointer`}
                >
                  Return to Skills Lab
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* AI Photorealistic Image Generation Modal */}
      {isAiImageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-2xl w-full p-6 shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col gap-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-purple-100 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 flex items-center justify-center border border-purple-200 dark:border-purple-800">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
                    Generate Photorealistic Image (AI Imagen 3)
                  </h3>
                  <p className="text-xs text-slate-500">
                    Synthesize high-fidelity clinical room photography tailored to this skill scenario
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsAiImageModalOpen(false)}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
              >
                <RotateCcw className="w-4 h-4 rotate-45" />
              </button>
            </div>

            <div className="flex flex-col gap-3 text-xs max-h-[75vh] overflow-y-auto pr-1">
              <div>
                <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Clinical Setting / Room Category
                </label>
                <select
                  value={aiEnvironment}
                  onChange={(e) => handleAiEnvironmentChange(e.target.value)}
                  className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-semibold"
                >
                  <option value="handwashing_station">Clinical Hand Hygiene & Sink Station (State Board Standard)</option>
                  <option value="bedside_room">Long-Term Care / Hospital Resident Bedside Room</option>
                  <option value="exam_room">Outpatient Clinic Examination & Consultation Suite</option>
                  <option value="phlebotomy_chair">Phlebotomy Draw Station & Blood Collection Chair</option>
                  <option value="icu_bay">Critical Care ICU Bay & High-Acuity Monitoring Room</option>
                </select>
              </div>

              {/* Instant Reference Preset for Hand Hygiene */}
              {/* Format Mode: Standard 16:9 vs 360° VR Panorama */}
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-purple-50/70 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                    Projection Format:
                  </span>
                </div>
                <div className="flex items-center gap-1 bg-white dark:bg-slate-900 p-1 rounded-lg border border-purple-200 dark:border-purple-800 text-xs">
                  <button
                    type="button"
                    onClick={() => setIs360PanoramaMode(false)}
                    className={`px-2.5 py-1 rounded-md font-bold transition-all ${
                      !is360PanoramaMode
                        ? 'bg-purple-600 text-white shadow-2xs'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                    }`}
                  >
                    16:9 Photography
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIs360PanoramaMode(true);
                      setAiPrompt(`A full 360-degree seamless equirectangular panoramic photograph of a modern clinical hospital ${aiEnvironment} for VR photosphere inspection, ultra-photorealistic clinical lighting.`);
                    }}
                    className={`px-2.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 ${
                      is360PanoramaMode
                        ? 'bg-purple-600 text-white shadow-2xs'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                    }`}
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>360° VR Panorama</span>
                  </button>
                </div>
              </div>

              {aiEnvironment === 'handwashing_station' && (
                <div className="p-3 rounded-xl bg-blue-50/70 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <img 
                      src="/images/environments/handwashing_station.jpg" 
                      alt="Handwashing Station"
                      className="w-14 h-10 object-cover rounded-md border border-blue-300 dark:border-blue-700 shrink-0" 
                    />
                    <div>
                      <span className="font-bold text-slate-900 dark:text-slate-100 block text-xs">
                        State Board Hand Hygiene Station Reference
                      </span>
                      <span className="text-[11px] text-slate-500 block">
                        Authentic scrub room with running water faucet, soap dispenser, towels, and bin.
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setGeneratedAiImageUrl('/images/environments/handwashing_station.jpg');
                      sound('success');
                    }}
                    className="px-2.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shrink-0 cursor-pointer shadow-2xs"
                  >
                    Use Reference Scene
                  </button>
                </div>
              )}

              {/* Customizable Clinical Fixtures / Components Checklist */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="font-bold text-slate-700 dark:text-slate-300">
                    Clinical Skill Components to Include in Photographic Scene
                  </label>
                  <span className="text-[11px] text-purple-600 dark:text-purple-400 font-semibold">
                    {selectedAiComponents.length} fixtures selected
                  </span>
                </div>

                {/* Quick Presets */}
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mr-1">Presets:</span>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('nurse_patient')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-purple-50 dark:bg-purple-950/60 border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900 cursor-pointer"
                  >
                    👩‍⚕️ Nurse + Patient Bedside
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('ambulation')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900 cursor-pointer"
                  >
                    🚶 Gait Belt & Ambulation
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('vitals')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900 cursor-pointer"
                  >
                    💓 Radial Pulse & Vitals
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('hygiene')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-teal-50 dark:bg-teal-950/60 border border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900 cursor-pointer"
                  >
                    🧼 Hand Hygiene Sink
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('all')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => applyComponentPreset('none')}
                    className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
                  >
                    Clear
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                  {CLINICAL_SKILL_FIXTURES.map(fixture => {
                    const isChecked = selectedAiComponents.includes(fixture.id);
                    return (
                      <button
                        key={fixture.id}
                        type="button"
                        onClick={() => handleToggleAiComponent(fixture.id)}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-[11px] font-medium border transition-colors ${
                          isChecked
                            ? 'bg-purple-50 dark:bg-purple-950/60 border-purple-300 dark:border-purple-700 text-purple-900 dark:text-purple-200'
                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:border-slate-300'
                        }`}
                      >
                        <div className={`w-3.5 h-3.5 rounded flex items-center justify-center border text-[9px] ${
                          isChecked 
                            ? 'bg-purple-600 border-purple-600 text-white' 
                            : 'border-slate-300 dark:border-slate-600'
                        }`}>
                          {isChecked && '✓'}
                        </div>
                        <span className="truncate">{fixture.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Visual Prompt Guidance (Auto-Updated from Components)
                </label>
                <textarea
                  rows={3}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Describe lighting, fixtures, patient presence, specific medical accessories..."
                  className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 leading-relaxed font-normal"
                />
              </div>

              {/* Action trigger button */}
              <div className="flex justify-end">
                <button
                  type="button"
                  id="submit-ai-image-gen-btn"
                  onClick={handleGenerateAiImage}
                  disabled={isGeneratingAiImage}
                  className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold text-xs flex items-center gap-2 shadow-md cursor-pointer"
                >
                  {isGeneratingAiImage ? (
                    <>
                      <RotateCcw className="w-4 h-4 animate-spin" />
                      <span>Generating with Imagen 3...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Generate Photorealistic Photography</span>
                    </>
                  )}
                </button>
              </div>

              {/* Generated Image Preview */}
              {generatedAiImageUrl && (
                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 flex flex-col gap-3 mt-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900 dark:text-slate-100">
                      Generated High-Fidelity Result:
                    </span>
                    <span className="px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">
                      Ready to Use
                    </span>
                  </div>
                  <div className="relative aspect-video rounded-xl overflow-hidden shadow-inner border border-slate-300 dark:border-slate-700 bg-black">
                    <img
                      src={generatedAiImageUrl}
                      alt="AI Generated Clinical Room"
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex items-center gap-1.5 pt-1">
                    <input
                      type="url"
                      value={wixUrlInput}
                      onChange={(e) => setWixUrlInput(e.target.value)}
                      placeholder="Paste Wix CDN Image URL (https://static.wixstatic.com/media/...)"
                      className="flex-1 p-2 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    />
                    <button
                      type="button"
                      onClick={handleApplyWixUrl}
                      className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shrink-0 cursor-pointer shadow-2xs"
                    >
                      Save Wix Link
                    </button>
                  </div>

                  <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = generatedAiImageUrl;
                        a.download = `imagen3_clinical_${aiEnvironment}_${Date.now()}.png`;
                        a.click();
                      }}
                      className="px-3 py-1.5 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 font-bold flex items-center gap-1.5 cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Download for Wix CDN</span>
                    </button>

                    <button
                      type="button"
                      id="apply-ai-generated-image-btn"
                      onClick={handleApplyGeneratedImage}
                      className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold flex items-center gap-1.5 shadow-md cursor-pointer"
                    >
                      <Check className="w-4 h-4" />
                      <span>Apply to Scenario & Stage</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
